import { apiStd } from './apiClient.js';

/**
 * Request a presigned URL for direct upload to S3 (POST /v1/file/upload-url).
 * Workspaces: `customer` = tenant slug; `file_name` and `file_type` = normalized file name and MIME type
 * (same rules as create and POST /v1/workspace/photo); `file_route` = `tenant_photo`.
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>} Envelope or body as returned by the API (via axios interceptors).
 */
export const getUploadUrl = (body) => apiStd.post('/v1/file/upload-url', body);

/**
 * Normalize the file name for the request (strips path separators).
 *
 * @param {string} [name]
 * @returns {string}
 */
export function normalizeUploadFileName(name) {
  const s = String(name ?? '').trim();
  const base = s || 'logo';
  return base.replace(/[/\\]/g, '_').replace(/\0/g, '');
}

/**
 * Read `upload_url` and `s3_path` from the API envelope or a plain object.
 *
 * @param {unknown} raw
 * @returns {{ uploadUrl: string, s3Path?: string }}
 */
export function parseSignedUploadResponse(raw) {
  const inner =
    raw && typeof raw === 'object' && raw.body != null && typeof raw.body === 'object'
      ? raw.body
      : raw && typeof raw === 'object' && raw.data != null && typeof raw.data === 'object'
        ? raw.data
        : raw;
  if (!inner || typeof inner !== 'object') {
    throw new Error('Invalid upload URL response');
  }
  const uploadUrl =
    typeof inner.upload_url === 'string'
      ? inner.upload_url
      : typeof inner.uploadUrl === 'string'
        ? inner.uploadUrl
        : null;
  if (!uploadUrl) {
    throw new Error('Missing upload_url in response');
  }
  const s3Path =
    typeof inner.s3_path === 'string'
      ? inner.s3_path
      : typeof inner.s3Path === 'string'
        ? inner.s3Path
        : undefined;
  return { uploadUrl, s3Path };
}

/**
 * PUT the binary to the presigned URL (direct `fetch` to the storage host).
 * Same as governance `useDocumentUpload.js`: only `method`, `body`, and `Content-Type`.
 *
 * @param {string} uploadUrl
 * @param {Blob | File} file
 * @returns {Promise<void>}
 */
export async function putFileToSignedUrl(uploadUrl, file) {
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
  }
}
